/**
 * A requirement set's documents in words (Settings › Bộ giấy tờ, a name-change request): a
 * document by its name, an alternatives group (`one_of`) by the names of its documents, a policy
 * a seeded name cites ("theo VN_SUBJECT_VERIFICATION") by the policy's name. Codes stay
 * secondary (a hover, a mono line under the name).
 *
 * Pure: tests/requirement-names.test.mjs.
 */

export interface RequiredDoc {
  code: string;
  name_vi: string;
  name_en: string;
  one_of?: string | null;
}

type Name = (d: { name_vi: string; name_en: string }) => string;

/** The other documents of `doc`'s alternatives group, by name ("… / …"). */
export function alternativesOf(doc: RequiredDoc, docs: readonly RequiredDoc[], name: Name): string {
  return docs
    .filter((d) => d.one_of && d.one_of === doc.one_of && d.code !== doc.code)
    .map(name)
    .join(' / ');
}

/** One entry of a request's missing documents (`<code>` or `group:<name>`, crates/lifecycle `requirement_sets::missing`), by name. */
export function missingName(entry: string, docs: readonly RequiredDoc[], name: Name, or: string): string {
  if (entry.startsWith('group:')) {
    const members = docs.filter((d) => d.one_of === entry.slice(6)).map(name);
    return members.length ? members.join(or) : entry.slice(6);
  }
  const doc = docs.find((d) => d.code === entry);
  return doc ? name(doc) : entry;
}

/** A policy key a name cites (`VN_SUBJECT_VERIFICATION`), by the policy's name when it has one. */
export function withPolicyNames(text: string, policyName: (key: string) => string | undefined): string {
  return text.replace(/\b[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+\b/g, (key) => policyName(key) ?? key);
}
